import { useEffect, useState, type FormEvent } from 'react';
import { isValidEmail } from '@canvas/utils';
import { parseEmailList } from '@/lib/pdf/pdfEmail';

interface PdfSendEmailModalProps {
  open: boolean;
  filename: string;
  busy?: boolean;
  onClose: () => void;
  onSend: (draft: { to: string; subject: string; message: string }) => void;
}

export default function PdfSendEmailModal({
  open,
  filename,
  busy,
  onClose,
  onSend,
}: PdfSendEmailModalProps) {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const base = filename || 'Document.pdf';
    setTo('');
    setSubject(`PDF: ${base}`);
    setMessage(`Please review the attached PDF (${base}).`);
    setError(null);
  }, [open, filename]);

  if (!open) return null;

  const close = () => {
    if (busy) return;
    onClose();
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const emails = parseEmailList(to);
    if (!emails.length) {
      setError('Enter at least one recipient email.');
      return;
    }
    if (emails.some((addr) => !isValidEmail(addr))) {
      setError('Enter a valid email address.');
      return;
    }
    setError(null);
    onSend({
      to: emails.join(', '),
      subject: subject.trim() || `PDF: ${filename}`,
      message: message.trim(),
    });
  };

  return (
    <div className="pdfe-modal-overlay" role="presentation" onClick={close}>
      <form
        className="pdfe-modal pdfe-email-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pdfe-email-title"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <header className="pdfe-modal-head">
          <h2 id="pdfe-email-title">Send to email</h2>
          <button
            type="button"
            className="pdfe-modal-close"
            onClick={close}
            aria-label="Close"
            disabled={busy}
          >
            ×
          </button>
        </header>

        <div className="pdfe-email-body">
          <p className="pdfe-email-hint">
            Opens your mail app with a download link (or the device share sheet). The PDF
            includes annotations and password protection if enabled.
          </p>
          <label className="pdfe-field">
            <span>To</span>
            <input
              type="text"
              inputMode="email"
              autoComplete="email"
              placeholder="name@example.com"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              disabled={busy}
              autoFocus
            />
          </label>
          <label className="pdfe-field">
            <span>Subject</span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={busy}
            />
          </label>
          <label className="pdfe-field">
            <span>Message</span>
            <textarea
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={busy}
            />
          </label>
          {error && <p className="pdfe-sig-modal-error">{error}</p>}
        </div>

        <footer className="pdfe-modal-foot">
          <button type="button" className="btn btn-secondary" onClick={close} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Preparing…' : 'Send'}
          </button>
        </footer>
      </form>
    </div>
  );
}
